/**
 * Google Analytics 4 Destination
 * Sends events to GA4 Measurement Protocol
 */

import { BaseDestination, DestinationConfig } from './base'
import { EventData } from '../hangarx'

export interface GA4Config extends DestinationConfig {
  measurementId: string
  apiSecret: string
  clientId?: string
}

export class GA4Destination extends BaseDestination {
  name = 'ga4'
  private measurementId: string
  private apiSecret: string
  private clientId: string
  
  constructor(config: GA4Config, debug: boolean = false) {
    super(config, debug)
    this.measurementId = config.measurementId
    this.apiSecret = config.apiSecret
    this.clientId = config.clientId || this.generateClientId()
  }
  
  async send(events: EventData[]): Promise<void> {
    if (!this.enabled) {
      this.log('Destination disabled, skipping')
      return
    }
    
    try {
      for (const event of events) {
        await this.sendEvent(event)
      }
      
      this.log(`Successfully sent ${events.length} events`)
    } catch (error) {
      this.logError('Failed to send events', error)
      throw error
    }
  }
  
  async identify(userId: string, traits?: Record<string, any>): Promise<void> {
    // GA4 doesn't have a separate identify call
    // User ID is sent with each event
    this.log('Identify called', { userId, traits })
  }
  
  async page(name?: string, properties?: Record<string, any>): Promise<void> {
    const event: EventData = {
      event_type: 'page_view',
      properties: {
        page_title: name || document.title,
        page_location: window.location.href,
        page_path: window.location.pathname,
        ...properties
      }
    }
    
    await this.sendEvent(event)
  }
  
  private async sendEvent(event: EventData): Promise<void> {
    const endpoint = `https://www.google-analytics.com/mp/collect?measurement_id=${this.measurementId}&api_secret=${this.apiSecret}`
    
    const payload = {
      client_id: event.anonymous_id || this.clientId,
      user_id: event.user_id,
      events: [{
        name: this.sanitizeEventName(event.event_type),
        params: {
          ...event.properties,
          session_id: event.session_id,
          engagement_time_msec: '100'
        }
      }]
    }
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
    
    if (!response.ok) {
      throw new Error(`GA4 HTTP ${response.status}: ${response.statusText}`)
    }
  }
  
  private sanitizeEventName(name: string): string {
    // GA4 event names must be alphanumeric with underscores
    return name.toLowerCase().replace(/[^a-z0-9_]/g, '_')
  }
  
  private generateClientId(): string {
    return `${Date.now()}.${Math.random().toString(36).substr(2, 9)}`
  }
}
